import React, { useEffect, useMemo, useState } from 'react'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import DualActionButtons from '../../../components/action_button/DualActionButtons'
import GuidanceNote from '../../../components/GuidanceNote'
import {
  dialogActionButtonSx,
  dialogActionsWithMetaSx,
  dialogContentWithBorderSx,
  dialogErrorTextSx,
  dialogHeaderWithSubtitleSx,
  dialogSelectedCountSx,
  dialogSubtitleSx,
  dialogTitleSx,
  getDialogCancelAction,
  guidanceNoteIconSx,
  guidanceNoteWarningAccentColors,
  guidanceNoteWarningAccentMessageSx,
  guidanceNoteWarningHeaderSx,
  guidanceNoteWarningSx
} from '../../../constants/dialogSx'
import { UI_COLOR_PRIMARY, UI_COLOR_PRIMARY_HOVER } from '../../../constants/uiColors'
import useRbacInfiniteUsers from '../../hooks/useRbacInfiniteUsers'
import { bulkAssignUserRoles } from '../../services/rbacApi'
import {
  RBAC_ADD_USERS_FILTER_ALL,
  RBAC_ADD_USERS_FILTER_UNASSIGNED,
  RBAC_ADD_USERS_LOADING_LABEL,
  RBAC_ADD_USERS_PAGE_SIZE,
  RBAC_ADD_USERS_PRIMARY_LABEL,
  RBAC_ADD_USERS_SCROLL_LOAD_THRESHOLD_PX,
  RBAC_ADD_USERS_SUBMIT_ERROR_MESSAGE,
  RBAC_ADD_USERS_TITLE,
  rbacAddUsersDialogPaperSx
} from '../constants/rbacAddUsersToRoleDialogConstants'
import { getRbacBulkSubmitErrorMessage } from '../rbac-bulk-reassign-roles-dialog/helpers'
import RbacAddUsersList from './RbacAddUsersList'
import RbacAddUsersToolbar from './RbacAddUsersToolbar'

export default function RbacAddUsersToRoleDialog({ open, onClose, onSuccess, role = null }) {
  const [filter, setFilter] = useState(RBAC_ADD_USERS_FILTER_UNASSIGNED)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [submitting, setSubmitting] = useState(false)

  const extraParams = useMemo(
    () => ({
      exclude_role_id: role?.id || undefined,
      unassigned: filter === RBAC_ADD_USERS_FILTER_UNASSIGNED ? true : undefined
    }),
    [filter, role?.id]
  )

  const {
    users,
    loadingUsers,
    loadingMore,
    searchInput,
    setSearchInput,
    loadError,
    setLoadError,
    handleListScroll
  } = useRbacInfiniteUsers({
    open,
    pageSize: RBAC_ADD_USERS_PAGE_SIZE,
    extraParams,
    scrollLoadThresholdPx: RBAC_ADD_USERS_SCROLL_LOAD_THRESHOLD_PX
  })

  const selectedCount = selectedIds.size
  const canSubmit = selectedCount > 0 && !submitting && !loadingUsers && Boolean(role?.id)
  const roleName = role?.name || 'este rol'

  useEffect(() => {
    if (open) return
    setFilter(RBAC_ADD_USERS_FILTER_UNASSIGNED)
    setSelectedIds(new Set())
    setSubmitting(false)
  }, [open])

  const handleClose = () => {
    if (submitting) return
    onClose?.()
  }

  const toggleUser = (userId) => {
    const id = String(userId)
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleFilterChange = (_event, nextFilter) => {
    if (!nextFilter) return
    setFilter(nextFilter)
  }

  const handleSubmit = async () => {
    if (!canSubmit) return

    const assignments = Array.from(selectedIds).map((userId) => ({
      user_id: Number(userId),
      role_ids: [role.id]
    }))

    try {
      setSubmitting(true)
      setLoadError('')
      await bulkAssignUserRoles(assignments)
      onSuccess?.(assignments.map((assignment) => assignment.user_id))
      onClose?.()
    } catch (err) {
      setLoadError(getRbacBulkSubmitErrorMessage(err) || RBAC_ADD_USERS_SUBMIT_ERROR_MESSAGE)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : handleClose}
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: rbacAddUsersDialogPaperSx }}
    >
      <DialogTitle sx={dialogHeaderWithSubtitleSx}>
        <Typography component="span" sx={dialogTitleSx}>
          {RBAC_ADD_USERS_TITLE}
        </Typography>
        <Typography sx={dialogSubtitleSx}>
          Selecciona los usuarios que quieres asignar a {roleName}.
        </Typography>
      </DialogTitle>
      <DialogContent sx={dialogContentWithBorderSx}>
        <RbacAddUsersToolbar
          filter={filter}
          onFilterChange={handleFilterChange}
          searchInput={searchInput}
          onSearchChange={setSearchInput}
        />

        {filter === RBAC_ADD_USERS_FILTER_ALL ? (
          <GuidanceNote
            icon={<WarningAmberOutlinedIcon />}
            title=""
            message={
              <>
                Los usuarios que selecciones pasarán a tener el rol <strong>{roleName}</strong>. Si
                ya tenían otro rol, será reemplazado por este.
              </>
            }
            accentColor={guidanceNoteWarningAccentColors.accentColor}
            backgroundColor={guidanceNoteWarningAccentColors.backgroundColor}
            textColor={guidanceNoteWarningAccentColors.textColor}
            sx={{
              ...guidanceNoteWarningSx,
              mt: 1.5,
              mb: 0
            }}
            headerSx={guidanceNoteWarningHeaderSx}
            iconSx={guidanceNoteIconSx}
            messageSx={guidanceNoteWarningAccentMessageSx}
          />
        ) : null}

        {loadError ? <Typography sx={dialogErrorTextSx}>{loadError}</Typography> : null}

        <RbacAddUsersList
          users={users}
          loadingUsers={loadingUsers}
          loadingMore={loadingMore}
          selectedIds={selectedIds}
          onToggleUser={toggleUser}
          onScroll={handleListScroll}
        />
      </DialogContent>
      <DialogActions sx={dialogActionsWithMetaSx}>
        <Typography sx={dialogSelectedCountSx}>
          {selectedCount} {selectedCount === 1 ? 'usuario seleccionado' : 'usuarios seleccionados'}
        </Typography>
        <DualActionButtons
          fullWidthOnMobile={false}
          minWidth={112}
          gap={1}
          secondaryAction={getDialogCancelAction({
            onClick: handleClose,
            disabled: submitting,
            sx: dialogActionButtonSx
          })}
          primaryAction={{
            label: RBAC_ADD_USERS_PRIMARY_LABEL,
            loadingLabel: RBAC_ADD_USERS_LOADING_LABEL,
            icon: null,
            color: UI_COLOR_PRIMARY,
            hoverColor: UI_COLOR_PRIMARY_HOVER,
            onClick: handleSubmit,
            disabled: !canSubmit,
            loading: submitting,
            sx: dialogActionButtonSx
          }}
        />
      </DialogActions>
    </Dialog>
  )
}
